import styled from "@emotion/styled";
import { ChangeEvent, Component, MouseEvent, createRef } from "react";

import { Table } from "components/table";
import { IFilters } from "constants/filters";
import { TicketsRoutes } from "constants/tickets/TicketsRoutes";
import { TicketsBatchAction } from "constants/tickets/batch-action";
import { Column } from "constants/tickets/columns";
import { TICKETS_MOUSE_ENTER_DELAY } from "constants/tickets/popper";
import { getIsMemberOfTicketTeam, routeSupportsBatchUpdate } from "helpers/tickets";
import { ITicket } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents";
import { ITeam } from "store/entities/teams";
import { ITicketsViewBatchUpdatePayloadData } from "store/views/tickets/interfaces";

import { TicketCheckbox } from "./TicketCheckbox";
import TicketsListFields from "./TicketsListFields";
import TicketsListHeader from "./TicketsListHeader";
import TicketsListArchiveBanner from "./above-list-banners/TicketsListArchiveBanner";
import { UndeliveredBanner } from "./above-list-banners/UndeliveredBanner";

interface IProps {
  agents: IAgent[];
  data: ITicket[];
  teams: ITeam[];
  currentAgent: IAgent;
  filters: IFilters;
  route: TicketsRoutes;
  selectedIds?: string[];
  isBatchUpdating: boolean;
  canCreateTickets: boolean;
  isDataFresh?: boolean;
  currentPage: number;
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBatchUpdate(ticketIds: string[], action: TicketsBatchAction, payload?: any): void;
  // @ts-expect-error TS(7010): 'onSelectedIdsUpdate', which lacks return-type ann... Remove this comment to see the full error message
  onSelectedIdsUpdate(ticketIds: string[]);
  // @ts-expect-error TS(7010): 'onSpamModalRequest', which lacks return-type anno... Remove this comment to see the full error message
  onSpamModalRequest(type: "spam" | "unspam", selectedIds: string[]);
}

interface IState {
  previewTicketId: string;
}

interface IWrapper {
  hasData: boolean;
}

const Wrapper = styled.div<IWrapper>`
  ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) =>
      props.hasData &&
      `
flex: 1;
overflow: hidden;
display: flex;
width: 100%;
`
  };
`;

let scrollPosition = 0;

function shouldDisplayBatchUpdate(
  canCreateTickets: boolean,
  route: TicketsRoutes,
  filters: IFilters,
  currentPage: number
): boolean {
  return canCreateTickets && routeSupportsBatchUpdate(route, filters, currentPage);
}

export class TicketsListTable extends Component<IProps, IState> {
  static defaultProps = {
    selectedIds: [],
  };

  // @ts-expect-error TS(2416): Property 'state' in type 'TicketsListTable' is not... Remove this comment to see the full error message
  state = {
    previewTicketId: null,
  };

  private shiftData: { start: ITicket | null } = { start: null };

  private tableContentRef = createRef<HTMLDivElement>();
  private previewShowTimeout = null;
  private scrollRestored = false;
  private shiftRange: { begin: ITicket | null; end: ITicket | null } = { begin: null, end: null };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidMount() {
    if (this.tableContentRef.current && scrollPosition > 0) {
      this.tableContentRef.current.scrollTop = scrollPosition;
      this.scrollRestored = true;
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  shouldComponentUpdate(nextProps: IProps) {
    return nextProps.isDataFresh !== false;
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentDidUpdate(prevProps: IProps) {
    if (prevProps.isDataFresh === false && this.props.isDataFresh === true) {
      if (this.scrollRestored) {
        this.scrollRestored = false;
      } else {
        // @ts-expect-error TS(2531): Object is possibly 'null'.
        this.tableContentRef.current.scrollTop = 0;
      }
    }
  }

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  componentWillUnmount() {
    // @ts-expect-error TS(2531): Object is possibly 'null'.
    scrollPosition = this.tableContentRef.current.scrollTop;
  }

  // this method is not memoized because all props changes
  // result in tickets list change anyway, so it can't be cached
  getAllCheckboxesChecked = (): boolean => {
    if (this.props.data.length === 0) {
      return false;
    }

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    return this.props.data.every((ticket: ITicket) => this.props.selectedIds.includes(ticket.id));
  };

  // this method is not memoized because all props changes
  // result in tickets list change anyway, so it can't be cached
  getAnyCheckboxChecked = (): boolean => {
    if (this.props.data.length === 0) {
      return false;
    }

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    return this.props.data.some((ticket: ITicket) => this.props.selectedIds.includes(ticket.id));
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handlePrimaryCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      this.props.onSelectedIdsUpdate(this.props.data.map((ticket: ITicket) => ticket.id));
    } else {
      this.props.onSelectedIdsUpdate([]);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, ticket: ITicket, shiftKey?: boolean) => {
    const { data, currentAgent, selectedIds } = this.props;

    let updateList = e.target.checked
      ? // @ts-expect-error TS(2488): Type 'string[] | undefined' must have a '[Symbol.i... Remove this comment to see the full error message
        [...this.props.selectedIds, ticket.id]
      : // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        selectedIds.filter(ticketId => ticketId !== ticket.id);

    if (this.shiftData.start && shiftKey) {
      if (this.shiftRange.begin === null && this.shiftRange.end === null) {
        this.shiftRange = { begin: this.shiftData.start, end: this.shiftData.start };
      }

      const selectableTickets = data.filter(t => getIsMemberOfTicketTeam(currentAgent, t));
      const anchorIndex = selectableTickets.indexOf(this.shiftData.start);
      const targetIndex = selectableTickets.indexOf(ticket);
      // @ts-expect-error TS(2345): Argument of type 'ITicket | null' is not assignabl... Remove this comment to see the full error message
      const currentRangeBegin = selectableTickets.indexOf(this.shiftRange.begin);
      // @ts-expect-error TS(2345): Argument of type 'ITicket | null' is not assignabl... Remove this comment to see the full error message
      const currentRangeEnd = selectableTickets.indexOf(this.shiftRange.end);

      if (anchorIndex === -1 || targetIndex === -1 || currentRangeBegin === -1 || currentRangeEnd === -1) {
        this.shiftData.start = null;
        this.shiftRange = { begin: null, end: null };
        return;
      }

      if (e.target.checked) {
        if (targetIndex < currentRangeBegin) {
          this.shiftRange.begin = ticket;
        } else if (targetIndex > currentRangeEnd) {
          this.shiftRange.end = ticket;
        }
      } else if (targetIndex >= currentRangeBegin && targetIndex <= currentRangeEnd) {
        this.shiftRange.end = selectableTickets[targetIndex - 1];
      }

      const oldRangeIDs = selectableTickets.slice(currentRangeBegin, currentRangeEnd + 1).map(t => t.id);
      const newRangeIDs = selectableTickets
        // @ts-expect-error TS(2345): Argument of type 'ITicket | null' is not assignabl... Remove this comment to see the full error message
        .slice(selectableTickets.indexOf(this.shiftRange.begin), selectableTickets.indexOf(this.shiftRange.end) + 1)
        .map(t => t.id);

      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      updateList = selectedIds.filter(id => !oldRangeIDs.includes(id)).concat(newRangeIDs);
    } else {
      this.shiftRange = { begin: null, end: null };
    }

    this.props.onSelectedIdsUpdate(updateList);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleBatchAction = (action: TicketsBatchAction, data?: ITicketsViewBatchUpdatePayloadData) => {
    // @ts-expect-error TS(2345): Argument of type 'string[] | undefined' is not ass... Remove this comment to see the full error message
    this.props.onBatchUpdate(this.props.selectedIds, action, data);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleMouseEnter = (ticketId: string) => (event: MouseEvent) => {
    event.persist();
    // @ts-expect-error TS(2322): Type 'Timeout' is not assignable to type 'null'.
    this.previewShowTimeout = setTimeout(() => {
      if ((event.target as HTMLTableCellElement).matches(":hover")) {
        this.setState({
          previewTicketId: ticketId,
        });
      }
    }, TICKETS_MOUSE_ENTER_DELAY);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleMouseLeave = () => {
    // @ts-expect-error TS(2769): No overload matches this call.
    clearTimeout(this.previewShowTimeout);

    this.setState({
      // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'string'.
      previewTicketId: null,
    });
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  getValidTickets = ({ data, currentAgent }: any) =>
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.filter((t: any) => !t.parentTicket && getIsMemberOfTicketTeam(currentAgent, t));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  allValidTicketsChecked = () => {
    const { currentAgent, data, selectedIds } = this.props;
    const validTickets = this.getValidTickets({ data, currentAgent });

    // @ts-expect-error TS(2532): Object is possibly 'undefined'.
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return validTickets.length > 0 && validTickets.every((t: any) => selectedIds.includes(t.id));
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  handleAllCheckboxChange = (event: any) => {
    if (event.target.checked) {
      const { currentAgent, data } = this.props;
      const validTickets = this.getValidTickets({ data, currentAgent });
      // FIXME: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.props.onSelectedIdsUpdate(validTickets.map((t: any) => t.id));
    } else {
      this.props.onSelectedIdsUpdate([]);
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  render() {
    const {
      agents,
      canCreateTickets,
      data,
      filters,
      teams,
      currentPage,
      currentAgent,
      route,
      selectedIds,
      onSpamModalRequest,
    } = this.props;

    return (
      <Wrapper hasData={data.length > 0}>
        <Table
          paddingBottom="3.5em"
          ref={this.tableContentRef}
          thead={
            <thead>
              <tr>
                {shouldDisplayBatchUpdate(canCreateTickets, route, filters, currentPage) && (
                  <TicketCheckbox
                    isPrimary
                    checked={this.allValidTicketsChecked()}
                    onChange={this.handleAllCheckboxChange}
                  />
                )}
                <TicketsListHeader
                  agents={agents}
                  teams={teams}
                  route={route}
                  isBatchUpdating={false}
                  onBatchActionChosen={this.handleBatchAction}
                  // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                  isAnyCheckboxChecked={selectedIds.length > 0}
                  onSpamModalRequest={onSpamModalRequest}
                />
              </tr>
            </thead>
          }
          divider={
            <>
              <TicketsListArchiveBanner route={route} />
              <UndeliveredBanner route={route} />
            </>
          }
          tbody={
            <tbody>
              {data.map((ticket: ITicket) => (
                <tr key={ticket.id} data-testid={`ticket-row-${ticket.id}`}>
                  {shouldDisplayBatchUpdate(canCreateTickets, route, filters, currentPage) && (
                    <TicketCheckbox
                      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                      checked={selectedIds.includes(ticket.id)}
                      shiftData={this.shiftData}
                      currentAgent={currentAgent}
                      isPrimary={false}
                      ticketId={ticket.id}
                      ticket={ticket}
                      onChange={(e, shiftKey) => this.handleCheckboxChange(e, ticket, shiftKey)}
                    />
                  )}
                  <TicketsListFields
                    route={route}
                    ticket={ticket}
                    agents={agents}
                    previewEnabled={ticket.id === this.state.previewTicketId}
                    columnTooltip={Column.Subject}
                    onMouseEnter={this.handleMouseEnter(ticket.id)}
                    onMouseLeave={this.handleMouseLeave}
                    onSpamModalRequest={onSpamModalRequest}
                  />
                </tr>
              ))}
            </tbody>
          }
        />
      </Wrapper>
    );
  }
}
